import React, { useEffect, useState } from 'react'
import { RouterProvider, createBrowserRouter, Navigate} from 'react-router-dom';

import LandingPage from './pages/LandingPage';
import ErrorPage from './pages/ErrorPage';
import SignIn from './pages/SignIn';
import DualColorPage from './pages/DualColorPage';
import Home from './pages/Home';
import AddHeart from './pages/AddHeart';
import ManageHeart from './pages/ManageHeart';
import ManageGroup from './pages/ManageGroup';
import NewGroup from './pages/NewGroup';
import NewGroupName from './pages/NewGroupName';
import NewGroupDone from './pages/NewGroupDone';
import NewGroupAddHearts from './pages/NewGroupAddHearts';
import AddHeartName from './pages/AddHeartName';
import Done from './pages/Done';
import AddHeartToGroup from './pages/AddHeartToGroup';
import JoinGroup from './pages/JoinGroup';
import SignUp from './pages/SignUp';
import MyAccount from './pages/MyAccount';
import AddGroup from './pages/AddGroup';
import LoadingScreen from './pages/LoadingScreen';
// React-Router
import PrivateRoute from './PrivateRoute';
// Redux
import { useDispatch, useSelector } from 'react-redux';
import { setAuthToken, updateUser, setLoading, setUserLoaded } from './redux/userSlice';
import Page2 from './pages/Page2';
import ResetPassword from './pages/ResetPassword';
import ResetPasswordDone from './pages/ResetPasswordDone';
import FAQ from './pages/FAQ';
// API
import { fetchUserData } from './api/apiFetcher';
import { initializeClient, cleanupClient } from './api/mqtt';
// Redux
import { updateUserOnReduxAndServer, clearUser } from './redux/userSlice';
//Firebase
import { initializeApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import { firebaseConfig } from './firebaseConfig';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import AddHeartVerify from './pages/AddHeartVerify';
// Constants
import { MQTT_BROKER_URL, EC2_SERVER_STATIC_IPV4 } from './constants';
import { RequireHeartVerified } from './route-guards/RequireHeartVerified';
import { RequireHeartSelected } from './route-guards/RequireHeartSelected';
import { useSnackbar } from 'notistack';
// Schemas
import { UserSchema } from 'lumas-shared-schemas'

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);

console.log("Environment: ", process.env.NODE_ENV);
console.log(`Lumas App Version: ${process.env.REACT_APP_VERSION}`);

function App() {

  // Ensure console.log is disabled in production
  // https://stackoverflow.com/questions/57224203/remove-console-log-statements-in-react-production-build
  if (process.env.NODE_ENV === "production")
    console.log = function no_console() {};

  const auth = getAuth();
  const dispatch = useDispatch();
  const groups = useSelector((state) => state.group.groups);
  const hearts = useSelector((state) => state.heart.hearts);
  // const [groupFetchComplete, setGroupFetchComplete] = useState(false);
  const groupHearts = useSelector((state) => state.heart.groupHearts);
  const isUserLoaded = useSelector((state) => state.user.isUserLoaded);
  const loading = useSelector((state) => state.user.loading);

  const {enqueueSnackbar} = useSnackbar() // for alert banners

  // Old
  useEffect(() => {
    // Define an async function inside useEffect
    const handleAuthStateChange = async (firebaseUser) => {
      if (firebaseUser) {
        // User is signed in
        console.log("Firebase user found: ", firebaseUser);
        setAuthToken(firebaseUser);

        const userEmail = firebaseUser.email;
        console.log("User email: ", userEmail);

        // Fetch user data only if not already fetched (is this needed?)
        if(!isUserLoaded) {

          // Fetch user data from DynamoDB
          try {
            await fetchUserData(userEmail, dispatch, groups, hearts, setLoading, setUserLoaded, groupHearts);
          } catch (error) {
            if (error.response) {
              const status = error.response.status;

              if (status === 404) {
                console.warn("Firebase user found, but no DynamoDB user. Creating new user.");

                try {
                  // Create new user object in DynamoDB referencing the lumas-shared-schemas structure
                  const dynamoUserObject = UserSchema.parse({
                    email: userEmail,
                    username: firebaseUser.displayName,
                    groups: [],
                    hearts: []
                  });
  
                  await dispatch(updateUserOnReduxAndServer({
                    id: userEmail,
                    updatedUser: dynamoUserObject,
                    dispatch
                  }));
                } catch (err) {
                  console.error("Error creating new user in DynamoDB:", err);
                  enqueueSnackbar(`Error creating new user. Please try again later.`, {variant: 'error'})
                }

              } else if (status >= 500) {
                console.error("API Server error fetching user data. Avoiding user creation.", error);
                // show UI error banner
                enqueueSnackbar(`API Server unavailable. Please try again later.`, {variant: 'error'})
                dispatch(setLoading(false));
                return; // do not log user in if 500 error
              } else {
                console.error("Unexpected response while fetching user data:", error);
                enqueueSnackbar(`Error fetching user data.`, {variant: 'error'})
              }
            } else {
              console.error("Network error or no response from server:", error);
              enqueueSnackbar(`Network error. Likely need to validate Web App Server SSL certificate.`, {variant: 'error'})
            }
          }
        } else {
          console.log("User already loaded, not fetching user data again.");
        }
      } else {
        // User is signed out
        console.log("No Firebase user is signed in.");
        dispatch(clearUser()); // Clear user data if no user is authenticated
        dispatch(setUserLoaded(false));
      }
      dispatch(setLoading(false));
    };

    // Set up onAuthStateChanged listener
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      handleAuthStateChange(firebaseUser);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [auth]);

  // React Router
  const router = createBrowserRouter([
    // Development test pages
    { path: '/dualColorMode', element: <DualColorPage /> },
    { path: '/page-2', element: <Page2/> },
    // Redirect to /home if user is already signed in if userLoaded/groupFetchComplete
      { path: '/', element: isUserLoaded ? <Navigate to="/home" replace /> : <LandingPage />, errorElement: <ErrorPage /> },
      { path: '/login', element: isUserLoaded ? <Navigate to="/home" replace /> : <SignIn />, errorElement: <ErrorPage /> },
      { path: '/sign-up', element: isUserLoaded ? <Navigate to="/home" replace /> : <SignUp />, errorElement: <ErrorPage />},
      { path: '/reset-password', element: isUserLoaded ? <Navigate to="/home" replace /> : <ResetPassword />, errorElement: <ErrorPage />},
      { path: '/reset-password-done', element: isUserLoaded ? <Navigate to="/home" replace /> : <ResetPasswordDone />, errorElement: <ErrorPage />},
      { path: '/home', 
        element: 
        (
          <PrivateRoute>
            <Home />
          </PrivateRoute>
        ),
        errorElement: <ErrorPage />,
      },
      { path: '/faq', 
        element: 
        (
          <PrivateRoute>
            <FAQ />
          </PrivateRoute>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: '/add-heart',
        element: (
          <PrivateRoute>
            <AddHeart />
          </PrivateRoute>
        ),
      },
      {
        path: '/add-heart/verify',
        element: (
          <PrivateRoute>
            <RequireHeartSelected>
              <AddHeartVerify />
            </RequireHeartSelected>
          </PrivateRoute>
        ),
      },
      {
        path: '/add-heart/name',
        element: (
          <PrivateRoute>
            <RequireHeartVerified>
              <AddHeartName />
            </RequireHeartVerified>
          </PrivateRoute>
        ),
      },
      {
        path: '/add-heart/done',
        element: (
          <PrivateRoute>
            <RequireHeartVerified>
              <Done />
            </RequireHeartVerified>
          </PrivateRoute>
        ),
      },
      {
        path: '/add-group',
        element: (
          <PrivateRoute>
            <AddGroup />
          </PrivateRoute>
        ),
      },
      {
        path: '/new-group',
        element: (
          <PrivateRoute>
            <NewGroup />
          </PrivateRoute>
        ),
      },
      {
        path: '/new-group/name',
        element: (
          <PrivateRoute>
            <NewGroupName />
          </PrivateRoute>
        ),
      },
      {
        path: '/new-group/add-hearts',
        element: (
          <PrivateRoute>
            <NewGroupAddHearts />
          </PrivateRoute>
        ),
      },
      {
        path: '/new-group/done',
        element: (
          <PrivateRoute>
            <NewGroupDone />
          </PrivateRoute>
        ),
      },
      {
        path: '/join-group',
        element: (
          <PrivateRoute>
            <JoinGroup />
          </PrivateRoute>
        )
      },
      {
        path: '/join-group/done',
        element: (
          <PrivateRoute>
            <Done />
          </PrivateRoute>
        ),
      },
      {
        path: '/manage-heart/:heartId',
        element: (
          <PrivateRoute>
            <ManageHeart />
          </PrivateRoute>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: '/manage-heart/:heartId/add-to-group',
        element: (
          <PrivateRoute>
            <AddHeartToGroup />
          </PrivateRoute>
        ),
      },
      {
        path: '/manage-heart/:heartID/add-to-group/done',
        element: (
          <PrivateRoute>
            <Done />
          </PrivateRoute>
        ),
      },
      {
        path: '/my-account',
        element: (
          <PrivateRoute>
            <MyAccount />
          </PrivateRoute>
        ),
      },
      {
        path: '/manage-group/:groupId',
        element: (
          <PrivateRoute>
            <ManageGroup />
          </PrivateRoute>
        ), 
      },
      {
        path: '*',
        element: <ErrorPage />,
      },
    ]
  );

  // Connect to MQTT Client in the root component to make persistent across all routes
  useEffect(() => {
    initializeClient();

    return () => {
      console.log("Cleaning up the MQTT Client!");
      cleanupClient();
    };
  }, []);

  // Render loading screen while checking auth state
  if (loading) {
    return <LoadingScreen />;
  }

  return (
    // <ThemeProvider theme={createTheme()}>
      <RouterProvider router={router} />
    // </ThemeProvider>
  )
}

export default App

  // useEffect(() => {
  //   // needed because groups and hearts state in store is not updated immediately after fetchAndDispatchGroups, groupFetchComplete gives buffer for store to update
  //   console.log("Group fetch complete: ", groupFetchComplete);
  //   if (groupFetchComplete) {
  //     fetchAndDispatchGroupHearts(groups, dispatch, hearts, groupHearts);

  //     // TODO: Subscribe to color topics for all groups
  //     // const topics = []
  //     // groups.forEach((group) => topics.push(`${MQTT_TOPIC_PREFIX}${group.id}/color`));
  //     // subscribeToTopics(topics, dispatch, colorListener);

  //     // can only navigate to home after groupHearts are fetched
  //     console.log("Got group hearts (TODO: subscribe to MQTT topics), will set isUserLoaded to true to navigate onwards")
  //   }
  // }, [groupFetchComplete]); // Note: only re-run the effect if groupFetchComplete changes

  // onAuthStateChanged(auth, (user) => {
  //   if (user) {
  //     // User is signed in, handle user information
  //     console.log('Retrieved Firebase user data from local storage:', user);
  //   } else {
  //     // User is signed out, handle accordingly
  //     console.log('No Firebase user is signed in.');
  //   }
  // });